import { useState, useEffect, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import styled from 'styled-components';
import { useAuth } from '../../../context/AuthContext';
import { getCommissionSummary, getCommissionsByRealtor } from '../../../services/commissionService';
import { getWithdrawalsByRealtor } from '../../../services/withdrawalService';
import { getSiteSettings } from '../../../services/settingsService';
import { buildContractRevenueRows } from '../../../utils/revenueCalc';

const MANAGE_TABS = [
  { label: '고객', path: '/realtor/manage' },
  { label: '추천', path: '/realtor/manage/referral' },
  { label: '정산', path: '/realtor/manage/settlement' },
];

// ───── 스타일 ─────
const Wrap = styled.div`
  padding-bottom: ${({ theme }) => theme.spacing.lg};
`;

const HeaderArea = styled.div`
  margin: -${({ theme }) => theme.realtor.pagePadding} -${({ theme }) => theme.realtor.pagePadding} 0;
  padding: ${({ theme }) => theme.spacing.lg} ${({ theme }) => theme.realtor.pagePadding} ${({ theme }) => theme.spacing.base};
`;

const HeaderTitle = styled.h1`
  font-size: ${({ theme }) => theme.fontSize.xl};
  font-weight: ${({ theme }) => theme.fontWeight.bold};
  color: ${({ theme }) => theme.colors.text};
  margin: 0;
`;

const HeaderDesc = styled.p`
  font-size: ${({ theme }) => theme.fontSize.sm};
  color: #9CA3AF;
  font-family: 'Apple SD Gothic Neo', 'Noto Sans KR', sans-serif;
  font-weight: 500;
  letter-spacing: -0.2px;
  margin: ${({ theme }) => theme.spacing.xs} 0 ${({ theme }) => theme.spacing.base};
`;

const EarningsCard = styled.div`
  background: ${({ theme }) => theme.colors.primary};
  border-radius: ${({ theme }) => theme.radius.md};
  padding: ${({ theme }) => theme.spacing.md} ${({ theme }) => theme.spacing.base};
  text-align: left;
`;

const EarningsLabel = styled.p`
  font-size: ${({ theme }) => theme.fontSize.sm};
  color: rgba(255, 255, 255, 0.7);
`;

const EarningsAmount = styled.p`
  font-size: 22px;
  font-weight: ${({ theme }) => theme.fontWeight.extrabold};
  color: #fff;
  margin: 2px 0;
`;

const EarningsSub = styled.p`
  font-size: ${({ theme }) => theme.fontSize.sm};
  color: rgba(255, 255, 255, 0.7);
`;

const TabRow = styled.div`
  display: flex;
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};
  margin: ${({ theme }) => theme.spacing.lg} -${({ theme }) => theme.realtor.pagePadding} 0;
  padding: 0 ${({ theme }) => theme.realtor.pagePadding};
`;

const TabItem = styled.button`
  flex: 1;
  padding: 12px 0;
  font-size: ${({ theme }) => theme.fontSize.base};
  font-weight: ${({ $active, theme }) => $active ? theme.fontWeight.semibold : theme.fontWeight.regular};
  color: ${({ $active, theme }) => $active ? theme.colors.primary : theme.colors.textSecondary};
  background: none;
  border: none;
  border-bottom: 2px solid ${({ $active, theme }) => $active ? theme.colors.primary : 'transparent'};
  cursor: pointer;
  white-space: nowrap;
  margin-bottom: -1px;
`;

const SectionTitle = styled.h3`
  font-size: ${({ theme }) => theme.fontSize.lg};
  font-weight: ${({ theme }) => theme.fontWeight.bold};
  color: ${({ theme }) => theme.colors.text};
  margin: ${({ theme }) => theme.spacing.xl} 0 ${({ theme }) => theme.spacing.base};
`;

/* 수수료 그리드 */
/* KPI 그리드 (2x2) */
const KpiGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: ${({ theme }) => theme.spacing.md};
  margin-top: ${({ theme }) => theme.spacing.sm};
`;

const KpiCell = styled.div`
  background: ${({ theme }) => theme.colors.bgWhite};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.radius.md};
  padding: ${({ theme }) => theme.spacing.base} ${({ theme }) => theme.spacing.base};
`;

const KpiLabel = styled.p`
  font-size: ${({ theme }) => theme.fontSize.xs};
  color: ${({ theme }) => theme.colors.textSecondary};
  margin-bottom: ${({ theme }) => theme.spacing.xs};
`;

const KpiValue = styled.p`
  font-size: ${({ theme }) => theme.fontSize.xl};
  font-weight: ${({ theme }) => theme.fontWeight.bold};
  color: ${({ theme }) => theme.colors.text};
`;

const TotalRewardBox = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-top: ${({ theme }) => theme.spacing.md};
  padding: ${({ theme }) => theme.spacing.base} ${({ theme }) => theme.spacing.lg};
  background: ${({ theme }) => theme.colors.primary}10;
  border: 1px solid ${({ theme }) => theme.colors.primary}33;
  border-radius: ${({ theme }) => theme.radius.md};
`;

const TotalRewardLabel = styled.span`
  font-size: ${({ theme }) => theme.fontSize.base};
  font-weight: ${({ theme }) => theme.fontWeight.semibold};
  color: ${({ theme }) => theme.colors.text};
`;

const TotalRewardAmount = styled.span`
  font-size: ${({ theme }) => theme.fontSize.lg};
  font-weight: ${({ theme }) => theme.fontWeight.extrabold};
  color: ${({ theme }) => theme.colors.primary};
`;

/* 알림 토글 */
const AlertRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin: 0 -${({ theme }) => theme.realtor.pagePadding};
  padding: ${({ theme }) => theme.spacing.lg} ${({ theme }) => theme.realtor.pagePadding};
  border-top: 1px solid ${({ theme }) => theme.colors.borderLight};
  border-bottom: 1px solid ${({ theme }) => theme.colors.borderLight};
`;

const AlertInfo = styled.div``;

const AlertTitle = styled.p`
  font-size: ${({ theme }) => theme.fontSize.base};
  font-weight: ${({ theme }) => theme.fontWeight.bold};
  color: ${({ theme }) => theme.colors.text};
  margin-bottom: 2px;
`;

const AlertDesc = styled.p`
  font-size: ${({ theme }) => theme.fontSize.sm};
  color: #9CA3AF;
  font-family: 'Apple SD Gothic Neo', 'Noto Sans KR', sans-serif;
  font-weight: 500;
  letter-spacing: -0.2px;
`;

const ToggleTrack = styled.div`
  width: 48px;
  height: 28px;
  border-radius: 14px;
  position: relative;
  background: ${({ $on, theme }) => ($on ? theme.colors.primary : '#D1D5DB')};
  cursor: pointer;
  flex-shrink: 0;
`;

const ToggleThumb = styled.div`
  position: absolute;
  top: 3px;
  width: 22px;
  height: 22px;
  border-radius: 50%;
  background: #fff;
  left: ${({ $on }) => ($on ? '23px' : '3px')};
  transition: left 0.2s;
  box-shadow: 0 1px 3px rgba(0,0,0,0.15);
`;

/* 링크 리스트 */
const LinkList = styled.div`
  margin: 0 -${({ theme }) => theme.realtor.pagePadding};
  padding: 0 ${({ theme }) => theme.realtor.pagePadding};
  border-top: 6px solid ${({ theme }) => theme.colors.borderLight};
`;

const LinkItem = styled.button`
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: calc(100% + ${({ theme }) => theme.realtor.pagePadding} * 2);
  margin-left: -${({ theme }) => theme.realtor.pagePadding};
  padding: ${({ theme }) => theme.spacing.base} ${({ theme }) => theme.realtor.pagePadding};
  border: none;
  background: none;
  cursor: pointer;
  text-align: left;
`;

const LinkLabel = styled.span`
  font-size: ${({ theme }) => theme.fontSize.base};
  font-weight: ${({ theme }) => theme.fontWeight.regular};
  color: ${({ theme }) => theme.colors.text};
`;

const LinkArrow = styled.span`
  font-size: ${({ theme }) => theme.fontSize.sm};
  color: ${({ theme }) => theme.colors.textSecondary};
`;

/* 출금 버튼 */
const WithdrawBtn = styled.button`
  width: 100%;
  height: 48px;
  border: none;
  border-radius: ${({ theme }) => theme.radius.pill};
  background: ${({ theme }) => theme.colors.primary};
  color: #fff;
  font-size: ${({ theme }) => theme.fontSize.md};
  font-weight: ${({ theme }) => theme.fontWeight.semibold};
  cursor: pointer;
  white-space: nowrap;
  margin-top: ${({ theme }) => theme.spacing.lg};

  &:hover { opacity: 0.92; }
`;

const WithdrawHint = styled.p`
  font-size: ${({ theme }) => theme.fontSize.sm};
  color: #9CA3AF;
  font-family: 'Apple SD Gothic Neo', 'Noto Sans KR', sans-serif;
  font-weight: 500;
  letter-spacing: -0.2px;
  text-align: center;
  margin-top: ${({ theme }) => theme.spacing.sm};
`;

/* 전환 수익금표 */
const PriceSection = styled.div`
  margin: ${({ theme }) => theme.spacing.xxl} -${({ theme }) => theme.realtor.pagePadding} 0;
  padding: ${({ theme }) => theme.spacing.lg} ${({ theme }) => theme.realtor.pagePadding};
  background: #F3F4F6;
`;

const PriceSectionTitle = styled.h3`
  font-size: ${({ theme }) => theme.fontSize.lg};
  font-weight: ${({ theme }) => theme.fontWeight.bold};
  color: ${({ theme }) => theme.colors.text};
  margin-bottom: ${({ theme }) => theme.spacing.xs};
`;

const PriceSectionHeader = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: ${({ theme }) => theme.spacing.sm};
  margin-bottom: ${({ theme }) => theme.spacing.base};
`;

const AreaInputWrap = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  background: #fff;
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.radius.md};
  padding: 6px 10px;
  flex-shrink: 0;
`;

const AreaInput = styled.input`
  width: 48px;
  border: 0;
  outline: none;
  font-size: ${({ theme }) => theme.fontSize.base};
  font-weight: ${({ theme }) => theme.fontWeight.bold};
  color: ${({ theme }) => theme.colors.primary};
  text-align: right;
  background: transparent;
  &::-webkit-outer-spin-button, &::-webkit-inner-spin-button { -webkit-appearance: none; margin: 0; }
`;

const AreaUnit = styled.span`
  font-size: ${({ theme }) => theme.fontSize.sm};
  color: ${({ theme }) => theme.colors.textSecondary};
  font-weight: ${({ theme }) => theme.fontWeight.semibold};
`;

const FormulaBox = styled.div`
  margin-top: ${({ theme }) => theme.spacing.md};
  padding: ${({ theme }) => theme.spacing.base};
  background: #fff;
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.radius.md};
`;

const FormulaResultRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: ${({ theme }) => theme.spacing.sm} ${({ theme }) => theme.spacing.base};
  background: ${({ theme }) => theme.colors.primary}10;
  border-radius: ${({ theme }) => theme.radius.sm};
  font-size: ${({ theme }) => theme.fontSize.sm};
  color: ${({ theme }) => theme.colors.text};
  font-weight: ${({ theme }) => theme.fontWeight.semibold};
`;

const FormulaResult = styled.span`
  font-size: ${({ theme }) => theme.fontSize.lg};
  font-weight: ${({ theme }) => theme.fontWeight.bold};
  color: ${({ theme }) => theme.colors.primary};
`;

const FormulaHint = styled.p`
  margin: 6px 2px 0;
  font-size: 12px;
  color: #9CA3AF;
  font-family: 'Apple SD Gothic Neo', 'Noto Sans KR', sans-serif;
  font-weight: 500;
  letter-spacing: -0.2px;
`;

const PriceSectionDesc = styled.p`
  font-size: ${({ theme }) => theme.fontSize.sm};
  color: #9CA3AF;
  font-family: 'Apple SD Gothic Neo', 'Noto Sans KR', sans-serif;
  font-weight: 500;
  letter-spacing: -0.2px;
  margin-bottom: ${({ theme }) => theme.spacing.base};
`;

const PriceTable = styled.table`
  width: 100%;
  border-collapse: separate;
  border-spacing: 0;
  font-size: ${({ theme }) => theme.fontSize.xs};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.radius.pill};
  overflow: hidden;
`;

const PriceTh = styled.th`
  padding: ${({ theme }) => theme.spacing.sm} 6px;
  font-size: 11px;
  font-weight: ${({ theme }) => theme.fontWeight.medium};
  color: ${({ theme }) => theme.colors.textSecondary};
  text-align: left;
  white-space: nowrap;
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};

  &:not(:first-child) { text-align: right; }
`;

const PriceTd = styled.td`
  padding: ${({ theme }) => theme.spacing.sm} 6px;
  font-size: 11px;
  color: ${({ theme }) => theme.colors.text};
  background: #fff;
  white-space: nowrap;
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};

  &:not(:first-child) { text-align: right; }
`;

const PriceBlue = styled.span`
  color: ${({ theme }) => theme.colors.primary};
  font-weight: ${({ theme }) => theme.fontWeight.semibold};
`;

const MonthlyCard = styled.div`
  margin-top: ${({ theme }) => theme.spacing.lg};
  background: ${({ theme }) => theme.colors.bgWhite};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.radius.md};
  padding: ${({ theme }) => theme.spacing.base} ${({ theme }) => theme.spacing.lg};
`;

const MonthlyHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: ${({ theme }) => theme.spacing.sm};
`;

const MonthlyTitle = styled.span`
  font-size: ${({ theme }) => theme.fontSize.base};
  font-weight: ${({ theme }) => theme.fontWeight.bold};
  color: ${({ theme }) => theme.colors.text};
`;

const MonthlyCount = styled.span`
  font-size: ${({ theme }) => theme.fontSize.xs};
  color: ${({ theme }) => theme.colors.textSecondary};
`;

const MonthlyRow = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: ${({ theme }) => theme.spacing.sm};
`;

const MonthlyCell = styled.div`
  text-align: left;
`;

const MonthlyLabel = styled.div`
  font-size: ${({ theme }) => theme.fontSize.xs};
  color: ${({ theme }) => theme.colors.textSecondary};
`;

const MonthlyValue = styled.div`
  font-size: ${({ theme }) => theme.fontSize.base};
  font-weight: ${({ theme }) => theme.fontWeight.bold};
  color: ${({ theme }) => theme.colors.text};
  margin-top: 2px;
`;

const WithdrawalsCard = styled.div`
  margin-top: ${({ theme }) => theme.spacing.md};
  background: ${({ theme }) => theme.colors.bgWhite};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.radius.md};
  padding: ${({ theme }) => theme.spacing.base} ${({ theme }) => theme.spacing.lg};
`;

const WithdrawalsTitle = styled.div`
  font-size: ${({ theme }) => theme.fontSize.base};
  font-weight: ${({ theme }) => theme.fontWeight.bold};
  color: ${({ theme }) => theme.colors.text};
  margin-bottom: ${({ theme }) => theme.spacing.sm};
`;

const WithdrawalRow = styled.div`
  display: grid;
  grid-template-columns: 1fr auto auto;
  gap: ${({ theme }) => theme.spacing.sm};
  align-items: center;
  padding: 8px 0;
  border-top: 1px solid ${({ theme }) => theme.colors.borderLight};

  &:first-of-type { border-top: none; }
`;

const WithdrawalDate = styled.span`
  font-size: ${({ theme }) => theme.fontSize.xs};
  color: ${({ theme }) => theme.colors.textSecondary};
`;

const WithdrawalAmount = styled.span`
  font-size: ${({ theme }) => theme.fontSize.sm};
  font-weight: ${({ theme }) => theme.fontWeight.bold};
  color: ${({ theme }) => theme.colors.text};
`;

const WithdrawalStatus = styled.span`
  padding: 2px 8px;
  border-radius: 999px;
  font-size: ${({ theme }) => theme.fontSize.xs};
  font-weight: ${({ theme }) => theme.fontWeight.semibold};
  background: ${({ $status }) =>
    $status === 'completed' ? '#D1FAE5' :
    $status === 'rejected' ? '#FEE2E2' :
    $status === 'approved' ? '#DBEAFE' :
    '#FEF3C7'};
  color: ${({ $status }) =>
    $status === 'completed' ? '#065F46' :
    $status === 'rejected' ? '#991B1B' :
    $status === 'approved' ? '#1D4ED8' :
    '#92400E'};
`;

// ───── 컴포넌트 ─────
const SettlementStatus = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { userData } = useAuth();
  const [alertOn, setAlertOn] = useState(true);
  const [summary, setSummary] = useState({ withdrawable: 0, total: 0, conversion: 0, referral: 0, settled: 0 });
  const [commissions, setCommissions] = useState([]);
  const [withdrawals, setWithdrawals] = useState([]);
  const [areaInput, setAreaInput] = useState(20);
  const [settings, setSettings] = useState(null);

  useEffect(() => {
    if (!userData?.uid) return;
    Promise.all([
      getCommissionSummary(userData.uid).catch(() => null),
      getCommissionsByRealtor(userData.uid).catch(() => []),
      getWithdrawalsByRealtor(userData.uid).catch(() => []),
    ]).then(([s, list, wlist]) => {
      if (s) setSummary(s);
      setCommissions(list || []);
      setWithdrawals((wlist || []).slice(0, 3));
    });
  }, [userData?.uid]);

  useEffect(() => {
    getSiteSettings().then(setSettings).catch(() => {});
  }, []);

  const monthly = useMemo(() => {
    const now = new Date();
    const ym = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
    const acc = { conversion: 0, referral: 0, settled: 0, total: 0, count: 0 };
    commissions.forEach((c) => {
      const date = c.created_at?.toDate?.() || (c.created_at?.seconds ? new Date(c.created_at.seconds * 1000) : null);
      if (!date) return;
      const cym = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
      if (cym !== ym) return;
      const amt = c.amount || 0;
      acc.total += amt;
      acc.count += 1;
      if (c.is_settled) acc.settled += amt;
      if (c.type === 'conversion') acc.conversion += amt;
      if (c.type === 'referral') acc.referral += amt;
    });
    return { ...acc, ym };
  }, [commissions]);

  const revenue = useMemo(
    () => buildContractRevenueRows(settings, areaInput || 0),
    [settings, areaInput],
  );

  const withdrawalStatusLabel = (status) => {
    if (status === 'requested') return '신청';
    if (status === 'approved') return '승인';
    if (status === 'completed') return '완료';
    if (status === 'rejected') return '반려';
    return status || '';
  };

  return (
    <Wrap>
      <HeaderArea>
        <HeaderTitle>관리·정산</HeaderTitle>
        <HeaderDesc>매출과 고객 흐름을 한 눈에 확인하세요</HeaderDesc>
        <EarningsCard>
          <EarningsLabel>출금 가능 수익</EarningsLabel>
          <EarningsAmount>{summary.withdrawable.toLocaleString()}원</EarningsAmount>
          <EarningsSub>총 수수료 {summary.total.toLocaleString()}원</EarningsSub>
        </EarningsCard>
      </HeaderArea>

      <TabRow>
        {MANAGE_TABS.map((t) => (
          <TabItem
            key={t.path}
            $active={location.pathname === t.path}
            onClick={() => navigate(t.path)}
          >
            {t.label}
          </TabItem>
        ))}
      </TabRow>

      <SectionTitle>수익금 내역</SectionTitle>

      <KpiGrid>
        <KpiCell>
          <KpiLabel>예정수익금</KpiLabel>
          <KpiValue>{((summary.total || 0) - (summary.settled || 0)).toLocaleString()} 원</KpiValue>
        </KpiCell>
        <KpiCell>
          <KpiLabel>상담요청 수익금</KpiLabel>
          <KpiValue>{(summary.conversion || 0).toLocaleString()} 원</KpiValue>
        </KpiCell>
        <KpiCell>
          <KpiLabel>실수익금</KpiLabel>
          <KpiValue>{(summary.settled || 0).toLocaleString()} 원</KpiValue>
        </KpiCell>
        <KpiCell>
          <KpiLabel>추천수익금 (5%·1년)</KpiLabel>
          <KpiValue>{(summary.referral || 0).toLocaleString()} 원</KpiValue>
        </KpiCell>
      </KpiGrid>

      <TotalRewardBox>
        <TotalRewardLabel>총 리워드</TotalRewardLabel>
        <TotalRewardAmount>{(summary.total || 0).toLocaleString()} 원</TotalRewardAmount>
      </TotalRewardBox>

      <MonthlyCard>
        <MonthlyHeader>
          <MonthlyTitle>이번 달 ({monthly.ym})</MonthlyTitle>
          <MonthlyCount>{monthly.count}건</MonthlyCount>
        </MonthlyHeader>
        <MonthlyRow>
          <MonthlyCell>
            <MonthlyLabel>전환</MonthlyLabel>
            <MonthlyValue>{monthly.conversion.toLocaleString()}원</MonthlyValue>
          </MonthlyCell>
          <MonthlyCell>
            <MonthlyLabel>추천</MonthlyLabel>
            <MonthlyValue>{monthly.referral.toLocaleString()}원</MonthlyValue>
          </MonthlyCell>
          <MonthlyCell>
            <MonthlyLabel>정산완료</MonthlyLabel>
            <MonthlyValue>{monthly.settled.toLocaleString()}원</MonthlyValue>
          </MonthlyCell>
        </MonthlyRow>
      </MonthlyCard>

      {withdrawals.length > 0 && (
        <WithdrawalsCard>
          <WithdrawalsTitle>최근 출금 신청</WithdrawalsTitle>
          {withdrawals.map((w) => (
            <WithdrawalRow key={w.id}>
              <WithdrawalDate>{w.createdAt || '-'}</WithdrawalDate>
              <WithdrawalAmount>{(w.amount || 0).toLocaleString()}원</WithdrawalAmount>
              <WithdrawalStatus $status={w.status}>{withdrawalStatusLabel(w.status)}</WithdrawalStatus>
            </WithdrawalRow>
          ))}
        </WithdrawalsCard>
      )}

      <AlertRow>
        <AlertInfo>
          <AlertTitle>수익금 증가 시 알림</AlertTitle>
          <AlertDesc>수익금이 증가하면 즉시 알림을 보냅니다.</AlertDesc>
        </AlertInfo>
        <ToggleTrack $on={alertOn} onClick={() => setAlertOn(!alertOn)}>
          <ToggleThumb $on={alertOn} />
        </ToggleTrack>
      </AlertRow>

      <LinkList>
        <LinkItem onClick={() => navigate('/realtor/manage/settlement-check')}>
          <LinkLabel>정산 확인</LinkLabel>
          <LinkArrow>&gt;</LinkArrow>
        </LinkItem>
      </LinkList>

      <WithdrawBtn onClick={() => navigate('/realtor/mypage/withdraw')}>
        출금 신청하기
      </WithdrawBtn>
      <WithdrawHint>계좌 인증 완료 후 신청 가능합니다.</WithdrawHint>

      <PriceSection>
        <PriceSectionHeader>
          <div>
            <PriceSectionTitle>계약완료 수익 분배 테이블</PriceSectionTitle>
            <PriceSectionDesc>평수 입력 시 수익이 자동 계산됩니다. (기본 20평 기준)</PriceSectionDesc>
          </div>
          <AreaInputWrap>
            <AreaInput
              type="number"
              value={areaInput}
              onChange={(e) => setAreaInput(Math.max(0, Number(e.target.value) || 0))}
              min="0"
              max="200"
              inputMode="numeric"
            />
            <AreaUnit>평</AreaUnit>
          </AreaInputWrap>
        </PriceSectionHeader>

        <PriceTable>
          <thead>
            <tr>
              <PriceTh>구분</PriceTh>
              <PriceTh>전체수익금</PriceTh>
              <PriceTh>내 수익</PriceTh>
            </tr>
          </thead>
          <tbody>
            {revenue.rows.map((r, i) => (
              <tr key={r.name}>
                <PriceTd>{r.name}</PriceTd>
                <PriceTd>{revenue.totals[i].toLocaleString()} 원</PriceTd>
                <PriceTd>
                  <PriceBlue>{revenue.shares[i].toLocaleString()} 원</PriceBlue>
                </PriceTd>
              </tr>
            ))}
          </tbody>
        </PriceTable>
        <FormulaBox>
          <FormulaResultRow>
            <span>{areaInput || 0}평 단일 서비스 최대 수익</span>
            <FormulaResult>{((revenue.shares[0] || 0) + (revenue.shares[3] || 0) + (revenue.shares[5] || 0) + (revenue.shares[6] || 0)).toLocaleString()} 원</FormulaResult>
          </FormulaResultRow>
          <FormulaHint>포장이사 + 입주청소 + 인터넷·TV + 인테리어 시 수익 예시</FormulaHint>
        </FormulaBox>
      </PriceSection>
    </Wrap>
  );
};

export default SettlementStatus;
